import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Database, CheckCircle, XCircle, Clock, Server } from 'lucide-react';

const DataSources = () => {
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDataSources = async () => {
    const data = await apiFetch(`${API}/datasources`);
    if (data?._error) {
      console.error('Error fetching data sources:', data.message);
      setLoading(false);
      return;
    }
    setDataSources(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchDataSources();
  }, []);

  const statusIcons = {
    connected: <CheckCircle className="text-green-600" size={20} />,
    disconnected: <XCircle className="text-red-600" size={20} />,
    migrating: <Clock className="text-orange-600" size={20} />
  };

  const migrationStatusColors = {
    completed: 'bg-green-100 text-green-800',
    in_progress: 'bg-blue-100 text-blue-800',
    not_started: 'bg-slate-100 text-slate-800',
    delayed: 'bg-red-100 text-red-800'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="datasources-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">ESG Data Sources</h1>
        <p className="text-slate-600">14+ systems mapped to AWS ingestion architecture</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-slate-900">{dataSources.length}</div>
          <div className="text-sm text-slate-600">Total Sources</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-900">
            {dataSources.filter(s => s.migration_status === 'completed').length}
          </div>
          <div className="text-sm text-slate-600">Migrated</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-900">
            {dataSources.filter(s => s.migration_status === 'in_progress').length}
          </div>
          <div className="text-sm text-slate-600">In Progress</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-slate-900">
            {(dataSources.reduce((sum, s) => sum + s.data_quality_score, 0) / dataSources.length).toFixed(0)}%
          </div>
          <div className="text-sm text-slate-600">Avg Quality Score</div>
        </div>
      </div>

      {/* Data Sources List */}
      <div className="grid grid-cols-1 gap-4">
        {dataSources.map((source) => (
          <div key={source.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Database className="text-blue-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{source.name}</h3>
                  <p className="text-sm text-slate-600 mb-2">{source.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">Type:</span>
                    <span className="text-xs text-slate-700">{source.type}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  {statusIcons[source.connection_status]}
                  <span className="text-sm capitalize">{source.connection_status}</span>
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded ${migrationStatusColors[source.migration_status]}`}>
                  {source.migration_status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>

            {/* AWS Service Mapping */}
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Server className="text-orange-600" size={16} />
                <span className="text-xs font-semibold text-slate-600 uppercase">AWS Architecture</span>
              </div>
              <p className="text-sm text-slate-700">{source.aws_service_mapping}</p>
            </div>

            {/* Frameworks & Quality */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block mb-2">Supported Frameworks</span>
                <div className="flex flex-wrap gap-1">
                  {source.supported_frameworks.map((fw) => (
                    <span key={fw} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                      {fw}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block mb-2">Data Quality</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${source.data_quality_score}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-bold text-slate-900">{source.data_quality_score}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Architecture Note */}
      <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">AWS Migration Strategy</h3>
        <p className="text-sm text-blue-800">
          Each data source is mapped to specific <strong>AWS ingestion patterns</strong>:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-blue-800 list-disc list-inside">
          <li><strong>AWS Glue + S3</strong>: Batch ETL for structured data (HR, Emissions)</li>
          <li><strong>DMS + RDS/DocumentDB</strong>: Database migration with minimal downtime</li>
          <li><strong>Lambda + API Gateway</strong>: Real-time ingestion from vendor APIs</li>
          <li><strong>IoT Core + Kinesis</strong>: Streaming data from energy monitoring systems</li>
        </ul>
      </div>
    </div>
  );
};

export default DataSources;